import { useEffect, useRef, useState } from 'react';
import { useServers } from '../hooks/use-servers.js';
import { loadPrefs, savePrefs, type NotificationPrefs } from '../services/notification-prefs.js';
import { hasPermission, requestPermission } from '../services/push.js';
import { Button } from './button.js';
import { useApiClient } from '../hooks/use-api-client.js';
import { disablePush, enablePush, pushEnabledHere, pushLost, pushSupported, syncPushScope } from '../services/web-push-client.js';

// Notifications of this browser: which servers they follow. Each browser and each Chrome profile keeps its
// own choice (a phone following everything, one desktop profile per client).

export function NotificationSettings() {
  const { data: servers } = useServers();
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => loadPrefs());
  const [allowed, setAllowed] = useState(() => hasPermission());
  const supported = typeof window !== 'undefined' && 'Notification' in window;

  const api = useApiClient();
  const [pushOn, setPushOn] = useState(() => pushEnabledHere());
  const [pushBusy, setPushBusy] = useState(false);
  // Opened from the bell's « Notifications on this browser » row: bring the section into view
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (window.location.hash === '#browser-notifications') sectionRef.current?.scrollIntoView?.({ block: 'start' });
  }, []);

  const [pushError, setPushError] = useState<string | null>(() => (pushLost() && !pushEnabledHere() ? 'Notifications stopped on this device: turn them on again.' : null));

  useEffect(() => {
    savePrefs(prefs);
    // The server sends Web Push by this choice: keep its copy in step
    void syncPushScope(api.push, prefs).catch(() => undefined);
  }, [prefs, api]);

  async function togglePush() {
    setPushBusy(true);
    setPushError(null);
    try {
      if (pushOn) await disablePush(api.push);
      else await enablePush(api.push, prefs);
      setPushOn(!pushOn);
      setAllowed(hasPermission());
    } catch (err) {
      setPushError((err as Error).message);
    } finally {
      setPushBusy(false);
    }
  }

  function toggle(serverId: string) {
    setPrefs((p) => ({ ...p, servers: p.servers.includes(serverId) ? p.servers.filter((s) => s !== serverId) : [...p.servers, serverId] }));
  }

  return (
    <div id="browser-notifications" ref={sectionRef} className="card scroll-mt-6 rounded-lg p-6">
      <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Notifications on this browser</h2>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-text-3)' }}>
        Each browser and each Chrome profile keeps its own choice: a phone can follow every server, a desktop profile only its client's.
      </p>

      <fieldset className="mt-4 space-y-2">
        <legend className="sr-only">Servers to notify about</legend>
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text)' }}>
          <input type="radio" name="notification-scope" checked={prefs.scope === 'all'} onChange={() => setPrefs((p) => ({ ...p, scope: 'all' }))} />
          All servers
        </label>
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text)' }}>
          <input type="radio" name="notification-scope" checked={prefs.scope === 'servers'} onChange={() => setPrefs((p) => ({ ...p, scope: 'servers' }))} />
          Only these servers
        </label>
        {prefs.scope === 'servers' && (
          <ul className="ml-6 space-y-1">
            {(servers ?? []).map((s) => (
              <li key={s.id}>
                <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-2)' }}>
                  <input type="checkbox" checked={prefs.servers.includes(s.id)} onChange={() => toggle(s.id)} />
                  {s.organization || s.id}
                  {s.organization && s.organization !== s.id && <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{s.id}</span>}
                </label>
              </li>
            ))}
            {prefs.servers.length === 0 && (
              <li className="text-xs" style={{ color: 'var(--color-warn)' }}>No server selected: this browser shows no server notification.</li>
            )}
          </ul>
        )}
      </fieldset>

      <div className="mt-5 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>When aidev is closed</h3>
        {pushSupported() ? (
          <>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>
              {pushOn
                ? 'This device receives decisions even when no aidev window is open, for the servers chosen above.'
                : 'Receive decisions on this device even when no aidev window is open. On an iPhone, add aidev to the home screen first.'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button size="sm" variant={pushOn ? 'ghost' : 'primary'} disabled={pushBusy} onClick={() => void togglePush()}>
                {pushBusy ? 'Saving…' : pushOn ? 'Stop on this device' : 'Notify this device'}
              </Button>
              {pushError && <span role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>{pushError}</span>}
            </div>
          </>
        ) : (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>
            This browser cannot receive notifications when aidev is closed. On an iPhone, add aidev to the home screen and open it from there.
          </p>
        )}
      </div>

      <div className="mt-4 text-sm" style={{ color: 'var(--color-text-2)' }}>
        {!supported ? (
          'This browser cannot show desktop notifications; the bell in the header still lists them.'
        ) : allowed ? (
          'Desktop notifications are allowed for this browser.'
        ) : (
          <span className="flex flex-wrap items-center gap-3">
            Desktop notifications are not allowed yet.
            <Button size="sm" onClick={() => void requestPermission().then((p) => setAllowed(p === 'granted'))}>Allow desktop notifications</Button>
          </span>
        )}
      </div>
    </div>
  );
}
